import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button } from "react-bootstrap";
import { getBaseUrl } from "../helpers/catalog";
import cogoToast from "cogo-toast";
import { useLocation } from "react-router-dom";
import { FaClock, FaExclamationTriangle } from "react-icons/fa";
import axios from "axios";

const SessionTimeout = () => {
  const [lifetime, setLifetime] = useState(120); // Default 120 minutes
  const [showModal, setShowModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // Seconds left when modal shows
  
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);
  const location = useLocation();

  // Obtener configuración del backend cada vez que cambia la ruta o al montar
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Usar axios para aprovechar el interceptor global de 401
        const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
        if (!token) return;

        const response = await axios.get(`${getBaseUrl()}/api/settings/session-lifetime`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          const serverLifetime = parseInt(response.data.lifetime);
          if (!isNaN(serverLifetime) && serverLifetime > 0) {
              setLifetime(prev => {
                  if (prev !== serverLifetime) {
                      console.log(`[SessionTimeout] Configuración actualizada: ${serverLifetime} minutos`);
                      return serverLifetime;
                  }
                  return prev;
              });
          }
        }
      } catch (error) {
        console.error("[SessionTimeout] Error fetching session settings", error);
        // Si es 401, el interceptor de App.js lo manejará.
      }
    };
    fetchSettings();
  }, [location.pathname]);

  const logout = useCallback(async () => {
    console.log("[SessionTimeout] Ejecutando logout por inactividad");
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      if (token) {
        await axios.post(`${getBaseUrl()}/api/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      console.error("Error durante logout", e);
    }
    
    // Limpiar storage
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    
    setShowModal(false);
    cogoToast.info("Su sesión ha expirado por inactividad.", { position: "top-center", heading: "Sesión Expirada" });
    
    // Redirigir a login
    window.location.href = "/login-register";
  }, []);

  const checkInactivity = useCallback(() => {
    // Si no hay token, no hacemos nada (ya está deslogueado)
    const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
    
    // Actualizar estado de autenticación si cambia
    if (!!token !== isAuthenticated) {
        setIsAuthenticated(!!token);
    }

    if (!token) return;

    const now = Date.now();
    const inactiveTime = (now - lastActivityRef.current) / 1000 / 60; // Minutos
    
    // Debug periódico (opcional, quitar en prod si molesta, útil ahora)
    if (Math.floor(now / 1000) % 60 === 0) { // Log cada minuto aprox
        console.log(`[SessionTimeout] Inactividad: ${inactiveTime.toFixed(2)}m / Límite: ${lifetime}m`);
    }

    if (inactiveTime >= lifetime) {
      logout();
    } else if (inactiveTime >= lifetime - 1) { // Falta 1 minuto o menos
      if (!showModal) {
          console.log("[SessionTimeout] Mostrando advertencia de expiración");
          setShowModal(true);
      }
      // Calcular segundos restantes para mostrar en el contador
      const secondsRemaining = Math.max(0, Math.ceil((lifetime * 60) - ((now - lastActivityRef.current) / 1000)));
      setTimeLeft(secondsRemaining);
    } else {
        if(showModal) setShowModal(false); 
    }
  }, [lifetime, logout, showModal, isAuthenticated]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showModal) setShowModal(false);
  }, [showModal]);

  useEffect(() => {
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    const handleActivity = () => {
        // Solo reiniciar si no está el modal abierto, o si queremos que cualquier acción cierre el modal.
        // El requerimiento es "muestre un modal", normalmente requiere acción explícita.
        // Pero si el usuario se mueve, está activo.
        // Si el modal está abierto, reiniciamos el timer? Sí, porque está activo.
        resetTimer();
    };

    // Agregar listeners
    events.forEach(event => window.addEventListener(event, handleActivity));
    
    // Iniciar intervalo
    checkIntervalRef.current = setInterval(checkInactivity, 1000);

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [resetTimer, checkInactivity]);

  // Si no está autenticado, no renderizar nada (pero los hooks siguen corriendo)
  if (!isAuthenticated) return null;

  return (
    <Fragment>
      <Modal 
        show={showModal} 
        onHide={() => resetTimer()} 
        centered 
        backdrop="static" 
        keyboard={false}
        className="session-timeout-modal"
      >
        <Modal.Body className="text-center p-5">
          <div className="mb-4 text-primary">
             <FaClock size={60} className="mb-2 heartbeat-animation" />
          </div>
          
          <h3 className="mb-3 font-weight-bold">¿Sigues ahí?</h3>
          
          <p className="mb-4 text-muted">
            Tu sesión expirará en <span className="text-danger font-weight-bold">{timeLeft}</span> segundos por inactividad.
          </p>

          <div className="d-flex justify-content-center gap-3">
            <Button variant="outline-primary" className="mr-2" onClick={logout}>
              Cerrar Sesión
            </Button>
            <Button variant="primary" className="px-4" onClick={resetTimer}>
              Seguir Conectado
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      
      <style>{`
        .heartbeat-animation {
          animation: heartbeat 1.5s ease-in-out infinite both;
        }
        @keyframes heartbeat {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </Fragment>
  );
};

export default SessionTimeout;
